'use client';

import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';

type PostKind = 'comment' | 'quote' | 'repost' | 'story';
type PostKindVariant = 'default' | 'table' | 'detail';

export type PostKindFlags = {
  isComment: boolean
  isQuote: boolean
  isRepost: boolean
  isStory: boolean
};

/** 광고 태그 한 장 — 키·라벨·색 */
export type PostAdTag = {
  key: string;
  label: string;
  bgcolor: string;
  color: string;
};

const KIND_COLORS: Record<PostKind, { bgcolor: string; color: string }> = {
  comment: { bgcolor: 'success.lighter', color: 'success.dark' },
  quote: { bgcolor: 'warning.lighter', color: 'warning.dark' },
  repost: { bgcolor: 'info.lighter', color: 'info.dark' },
  story: { bgcolor: 'error.lighter', color: 'error.dark' },
};

/**
 * 틱톡 광고 태그 — `adMeta`가 근거다.
 *
 * ⚠️ **두 축은 서로 독립이다.** `adType`이 한 줄(틱톡샵 | 협찬 | 없음), `adBoost`가
 * 또 한 줄(광고집행 | 없음)이라 한 게시물에 태그가 **둘까지** 붙는다.
 * `organic`·`unknown`은 태그가 없다 — 「광고가 아님」을 굳이 표시하지 않는다.
 *
 * ⚠️ `source === 'tiktok'`을 따로 보지 않는다. `adMeta.adType`이 실린 문서는 틱톡뿐이라
 * 값이 있으면 곧 틱톡이다. 다른 채널이 이 필드를 채우기 시작하면 그 채널에도 같은 뜻으로
 * 붙는 게 맞다.
 */
const AD_TYPE_TAGS: Record<string, PostAdTag> = {
  commission: { key: 'commission', label: '틱톡샵', bgcolor: 'pink.lighter', color: 'pink.dark' },
  paid: { key: 'paid', label: '협찬', bgcolor: 'purple.lighter', color: 'purple.dark' },
};

const AD_BOOST_TAG: PostAdTag = {
  key: 'adBoost',
  label: '광고집행',
  bgcolor: 'violet.lighter',
  color: 'violet.dark',
};

/** 칩 크기만 정한다 — 색은 종류별 표가 따로 얹는다 */
export function buildPostChipSizeSx(variant: PostKindVariant) {
  if (variant === 'table') return { height: 22, fontSize: '11px', fontWeight: 600 };
  if (variant === 'detail') return { height: 26, typography: 'uiMeta', fontWeight: 600 };
  return { height: 20, typography: 'uiMeta', fontWeight: 600 };
}

function buildChipSx(variant: PostKindVariant, kind: PostKind) {
  return { ...buildPostChipSizeSx(variant), ...KIND_COLORS[kind] };
}

/** @param {Object} post */
export function getPostKindFlags(post?: {
  isComment?: unknown
  isQuote?: unknown
  isRepost?: unknown
  isStory?: unknown
} | null): PostKindFlags {
  return {
    isComment: Boolean(post?.isComment),
    isQuote: Boolean(post?.isQuote),
    isRepost: Boolean(post?.isRepost),
    isStory: Boolean(post?.isStory),
  };
}

export function hasAnyPostKindChip(flags?: Partial<PostKindFlags> | null) {
  return Boolean(
    flags?.isComment
    || flags?.isQuote
    || flags?.isRepost
    || flags?.isStory,
  );
}

/**
 * `adMeta` → 광고 태그 목록. 붙을 게 없으면 빈 배열.
 *
 * ES 원본(`_source.adMeta`)과 `transformPostData`가 정규화한 값의 **모양이 같아서**
 * 변환을 거치는 경로(카드·상세)와 거치지 않는 경로(키워드 게시물 탭·미리보기)가
 * 같은 함수를 쓴다.
 *
 * @param post 게시물
 * @returns 붙일 광고 태그 목록
 */
export function getPostAdTags(post?: {
  adMeta?: { adType?: unknown; adBoost?: unknown } | null
} | null): PostAdTag[] {
  const adMeta = post?.adMeta;
  if (!adMeta || typeof adMeta !== 'object') return [];

  const tags: PostAdTag[] = [];
  const typeTag = AD_TYPE_TAGS[String(adMeta.adType ?? '').trim().toLowerCase()];
  if (typeTag) tags.push(typeTag);
  if (adMeta.adBoost === true) tags.push(AD_BOOST_TAG);
  return tags;
}

/**
 * 게시물 태그 한 줄 — 유형(댓글 / 인용 / 리포스트 / 스토리)과 광고(틱톡샵 / 협찬 / 광고집행).
 *
 * ⚠️ **붙을 태그가 하나도 없으면 `null`을 돌려준다.** 부르는 쪽이 미리 세어 볼 필요가 없다.
 *
 * ⚠️ **두 축 다 화면을 안 가린다.** 글이 그 값을 갖고 있으면 어디서나 붙는다 —
 * 표시와 거르기는 다른 일이고, 거를 수 없는 화면에서도 「이 글이 무엇인가」는 알려 준다.
 * 값이 안 실려 오면 태그가 조용히 사라지므로, 카드를 그리는 화면의 `_source` 스펙에
 * `adMeta` 가 있는지는 `src/constants/moment-fetch-fields.ts` 에서 본다.
 */
export default function PostKindChips({
  post,
  flags: flagsProp,
  adTags: adTagsProp,
  variant = 'default',
  size = 'small',
  stackSx,
  iconMap,
}: {
  /** 유형 플래그·광고 태그를 여기서 뽑는다 */
  post?: Parameters<typeof getPostKindFlags>[0] & Parameters<typeof getPostAdTags>[0]
  /** 유형 플래그 직접 전달 (isQuote prop 오버라이드 등) */
  flags?: Partial<PostKindFlags> | null
  /** 광고 태그 직접 전달 */
  adTags?: PostAdTag[] | null
  variant?: PostKindVariant
  size?: 'small' | 'medium'
  stackSx?: Record<string, unknown>
  iconMap?: Partial<Record<PostKind, React.ReactElement>>
}) {
  const flags = flagsProp ?? getPostKindFlags(post);
  const adTags = adTagsProp ?? getPostAdTags(post);
  if (!hasAnyPostKindChip(flags) && adTags.length === 0) return null;

  const renderChip = (kind: PostKind, label: string) => (
    <Chip
      key={kind}
      label={label}
      size={size}
      icon={iconMap?.[kind]}
      sx={buildChipSx(variant, kind)}
    />
  );

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      sx={{ flexWrap: 'wrap', ...stackSx }}
    >
      {flags.isComment && renderChip('comment', '댓글')}
      {flags.isQuote && renderChip('quote', '인용')}
      {flags.isRepost && renderChip('repost', '리포스트')}
      {flags.isStory && renderChip('story', '스토리')}
      {adTags.map((tag) => (
        <Chip
          key={tag.key}
          label={tag.label}
          size={size}
          sx={{ ...buildPostChipSizeSx(variant), bgcolor: tag.bgcolor, color: tag.color }}
        />
      ))}
    </Stack>
  );
}
